import React, { useContext } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTachometerAlt,
  faCreditCard,
  faChartBar,
  faCog,
  faMapMarkerAlt,
  faCalculator,
  faGasPump,
  faUsers,
  faHandshake,
  faExchangeAlt,
  faWallet,
} from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from './AuthContext';

const Sidebar = ({ theme }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  
  const handleNavigate = (path) => {
    navigate(path);
  };

  const userRoles = currentUser?.Roles || [];

  return (
    <>
      <div className="hidden xl:block sidebar-fixed">
        <div className="sidebar-fixed">
          <div 
            className="w-64 flex flex-col h-screen"
            style={{ backgroundColor: theme.sidebarBg || '#ffffff' }}
          >
            {/* Company Logo Header */}
            <div className="p-6 pb-4">
              <div className="flex justify-center mb-4">
                <img src={theme.logo} alt="Company Logo" className="responsive-img max-h-12" />
              </div>
              <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
            </div>

            {/* Scrollable Menu Content */}
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              {/* Main Navigation Section */}
              {!userRoles.includes('Driver') && !currentUser?.isAffiliate && (
                <div className="mb-6">
                  <div className="mb-3">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 mb-2">
                      Main Navigation
                    </h3>
                  </div>
                  
                  <nav className="space-y-1">
                    {/* Dashboard */}
                    <button
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                        location.pathname === "/dashboard" ? "shadow-md" : "hover:shadow-sm"
                      }`}
                      onClick={() => handleNavigate("/dashboard")}
                      style={{
                        ...(location.pathname === "/dashboard" 
                          ? {
                              backgroundColor: theme.sidebarHoverBg || `${theme.primary}15`,
                              color: theme.primary,
                              borderLeft: `4px solid ${theme.primary}`
                            }
                          : { color: theme.menuText })
                      }}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                        location.pathname === "/dashboard" 
                          ? "" 
                          : "group-hover:bg-gray-100"
                      }`}
                      style={location.pathname === "/dashboard" ? {
                        backgroundColor: `${theme.primary}20`
                      } : {}}>
                        <FontAwesomeIcon icon={faTachometerAlt} className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-sm">Dashboard</span>
                    </button>

                    {/* Fuel Cards */}
                    <button
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                        location.pathname === "/usercards" ? "shadow-md" : "hover:shadow-sm"
                      }`}
                      onClick={() => handleNavigate("/usercards")}
                      style={{
                        ...(location.pathname === "/usercards" 
                          ? {
                              backgroundColor: theme.sidebarHoverBg || `${theme.primary}15`,
                              color: theme.primary,
                              borderLeft: `4px solid ${theme.primary}`
                            }
                          : { color: theme.menuText })
                      }}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                        location.pathname === "/usercards" 
                          ? "" 
                          : "group-hover:bg-gray-100"
                      }`}
                      style={location.pathname === "/usercards" ? {
                        backgroundColor: `${theme.primary}20`
                      } : {}}>
                        <FontAwesomeIcon icon={faCreditCard} className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-sm">Fuel Cards</span>
                    </button>

                    {/* Users */}
                    <button
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                        location.pathname === "/userusers" ? "shadow-md" : "hover:shadow-sm"
                      }`}
                      onClick={() => handleNavigate("/userusers")}
                      style={{
                        ...(location.pathname === "/userusers" 
                          ? {
                              backgroundColor: theme.sidebarHoverBg || `${theme.primary}15`,
                              color: theme.primary,
                              borderLeft: `4px solid ${theme.primary}`
                            }
                          : { color: theme.menuText })
                      }}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                        location.pathname === "/userusers" 
                          ? "" 
                          : "group-hover:bg-gray-100"
                      }`}
                      style={location.pathname === "/userusers" ? {
                        backgroundColor: `${theme.primary}20`
                      } : {}}>
                        <FontAwesomeIcon icon={faUsers} className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-sm">Users</span>
                    </button>

                    {/* Fuel Transactions */}
                    <button
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                        location.pathname === "/usertransactions" ? "shadow-md" : "hover:shadow-sm"
                      }`}
                      onClick={() => handleNavigate("/usertransactions")}
                      style={{
                        ...(location.pathname === "/usertransactions" 
                          ? {
                              backgroundColor: theme.sidebarHoverBg || `${theme.primary}15`,
                              color: theme.primary,
                              borderLeft: `4px solid ${theme.primary}`
                            }
                          : { color: theme.menuText })
                      }}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                        location.pathname === "/usertransactions" 
                          ? "" 
                          : "group-hover:bg-gray-100"
                      }`}
                      style={location.pathname === "/usertransactions" ? {
                        backgroundColor: `${theme.primary}20`
                      } : {}}>
                        <FontAwesomeIcon icon={faGasPump} className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-sm">Fuel Transactions</span>
                    </button>

                    {/* Accounting */}
                    <button
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                        location.pathname === "/useraccounting" ? "shadow-md" : "hover:shadow-sm"
                      }`}
                      onClick={() => handleNavigate("/useraccounting")}
                      style={{
                        ...(location.pathname === "/useraccounting" 
                          ? {
                              backgroundColor: theme.sidebarHoverBg || `${theme.primary}15`,
                              color: theme.primary,
                              borderLeft: `4px solid ${theme.primary}`
                            }
                          : { color: theme.menuText })
                      }}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                        location.pathname === "/useraccounting" 
                          ? "" 
                          : "group-hover:bg-gray-100"
                      }`}
                      style={location.pathname === "/useraccounting" ? {
                        backgroundColor: `${theme.primary}20`
                      } : {}}>
                        <FontAwesomeIcon icon={faWallet} className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-sm">Accounting</span>
                    </button>

                    {/* Reports */}
                    <button
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                        location.pathname === "/userreporting" ? "shadow-md" : "hover:shadow-sm"
                      }`}
                      onClick={() => handleNavigate("/userreporting")}
                      style={{
                        ...(location.pathname === "/userreporting" 
                          ? {
                              backgroundColor: theme.sidebarHoverBg || `${theme.primary}15`,
                              color: theme.primary,
                              borderLeft: `4px solid ${theme.primary}`
                            }
                          : { color: theme.menuText })
                      }}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                        location.pathname === "/userreporting" 
                          ? "" 
                          : "group-hover:bg-gray-100"
                      }`}
                      style={location.pathname === "/userreporting" ? {
                        backgroundColor: `${theme.primary}20`
                      } : {}}>
                        <FontAwesomeIcon icon={faChartBar} className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-sm">Reports</span>
                    </button>
                  </nav>
                </div>
              )}

              {/* General Section */}
              <div className="mb-6">
                <div className="mb-3">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 mb-2">
                    General
                  </h3>
                </div>
                
                <nav className="space-y-1">
                  {/* Locations */}
                  <button
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                      location.pathname === "/locationfinder" ? "shadow-md" : "hover:shadow-sm"
                    }`}
                    onClick={() => handleNavigate("/locationfinder")}
                    style={{
                      ...(location.pathname === "/locationfinder" 
                        ? {
                            backgroundColor: theme.sidebarHoverBg || `${theme.primary}15`,
                            color: theme.primary,
                            borderLeft: `4px solid ${theme.primary}`
                          }
                        : { color: theme.menuText })
                    }}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                      location.pathname === "/locationfinder" 
                        ? "" 
                        : "group-hover:bg-gray-100"
                    }`}
                    style={location.pathname === "/locationfinder" ? {
                      backgroundColor: `${theme.primary}20`
                    } : {}}>
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="w-4 h-4" />
                    </div>
                    <span className="font-medium text-sm">Locations</span>
                  </button>

                  {/* Affiliate Dashboard */}
                  {currentUser?.isAffiliate && (
                    <button
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                        location.pathname === "/affiliate-dashboard" ? "shadow-md" : "hover:shadow-sm"
                      }`}
                      onClick={() => handleNavigate("/affiliate-dashboard")}
                      style={{
                        ...(location.pathname === "/affiliate-dashboard" 
                          ? {
                              backgroundColor: theme.sidebarHoverBg || `${theme.primary}15`,
                              color: theme.primary,
                              borderLeft: `4px solid ${theme.primary}`
                            }
                          : { color: theme.menuText })
                      }}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                        location.pathname === "/affiliate-dashboard" 
                          ? "" 
                          : "group-hover:bg-gray-100"
                      }`}
                      style={location.pathname === "/affiliate-dashboard" ? {
                        backgroundColor: `${theme.primary}20`
                      } : {}}>
                        <FontAwesomeIcon icon={faHandshake} className="w-4 h-4" />
                      </div>
                      <span className="font-medium text-sm">Affiliate Portal</span>
                    </button>
                  )}
                </nav>
              </div>

              {/* Administration Section */}
              {((currentUser?.hasSubaccounts && (currentUser?.isAdmin || currentUser?.isPolicyAdmin)) || 
                (currentUser?.isAdmin || currentUser?.isContractAdmin)) && (
                <div>
                  <div className="mb-3">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 mb-2">
                      Administration
                    </h3>
                  </div>
                  
                  <nav className="space-y-1">
                    {/* Account Settings */}
                    {(currentUser?.hasSubaccounts && (currentUser?.isAdmin || currentUser?.isPolicyAdmin)) && (
                      <button
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                          location.pathname === "/accountmanager" ? "shadow-md" : "hover:shadow-sm"
                        }`}
                        onClick={() => handleNavigate("/accountmanager")}
                        style={{
                          ...(location.pathname === "/accountmanager" 
                            ? {
                                backgroundColor: theme.sidebarHoverBg || `${theme.primary}15`,
                                color: theme.primary,
                                borderLeft: `4px solid ${theme.primary}`
                              }
                            : { color: theme.menuText })
                        }}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                          location.pathname === "/accountmanager" 
                            ? "" 
                            : "group-hover:bg-gray-100"
                        }`}
                        style={location.pathname === "/accountmanager" ? {
                          backgroundColor: `${theme.primary}20`
                        } : {}}>
                          <FontAwesomeIcon icon={faCog} className="w-4 h-4" />
                        </div>
                        <span className="font-medium text-sm">Account Settings</span>
                      </button>
                    )}

                    {/* Admin Settings */}
                    {(currentUser?.isAdmin || currentUser?.isContractAdmin) && (
                      <button
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                          location.pathname === "/admin-settings" ? "shadow-md" : "hover:shadow-sm"
                        }`}
                        onClick={() => handleNavigate("/admin-settings")}
                        style={{
                          ...(location.pathname === "/admin-settings" 
                            ? {
                                backgroundColor: theme.sidebarHoverBg || `${theme.primary}15`,
                                color: theme.primary,
                                borderLeft: `4px solid ${theme.primary}`
                              }
                            : { color: theme.menuText })
                        }}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                          location.pathname === "/admin-settings" 
                            ? "" 
                            : "group-hover:bg-gray-100"
                        }`}
                        style={location.pathname === "/admin-settings" ? {
                          backgroundColor: `${theme.primary}20`
                        } : {}}>
                          <FontAwesomeIcon icon={faCog} className="w-4 h-4" />
                        </div>
                        <span className="font-medium text-sm">Admin Settings</span>
                      </button>
                    )}
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
